import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { rutaParaResponder } from '../lib/emailThreads';
import {
  AlertTriangle, Ban, Banknote, Bot, Box, CalendarDays, Car, Check, CheckCircle2, Clapperboard,
  Coins, Construction, DoorClosed, Eye, FileText, Flag, Hammer, Hand, HardHat, Handshake, Home,
  Hourglass, Inbox, Landmark, Link2, LockOpen, Mail, Mailbox, MessageSquare, Paperclip, PenLine,
  Pin, Plus, Receipt, Rocket, RotateCcw, Ruler, ScrollText, Search, Send, Shuffle, ThumbsUp,
  Timer, Video, X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { fmtDateTimeEastern, formatUsd } from '../lib/format';
import { EmailCard, type EmailAttachment, type EmailLike } from './email/EmailThread';

/**
 * Vista reutilizable de una LÍNEA DE TIEMPO animada (rail vertical + nodos con icono/color).
 * La usan el timeline de estimación (Supplements) y el de dinero (Payments). Todos los "ver"
 * se muestran EMBEBIDOS (iframe/modal, no nueva pestaña): archivo/estimado, invoice, cheque,
 * prueba de autorización, comprobante de pago, y el email. Además cada item con documento
 * tiene "Send to CO/HO" para mandarlo por email desde el correo del depto (payments/supplements).
 */

export type TimelineKind = 'stage' | 'sketch' | 'estimate' | 'analysis' | 'email' | 'update' | 'check' | 'invoice' | 'payment' | 'payStatus' | 'approval'
  // kinds del timeline de INSPECCIONES (InspectionTimeline)
  | 'request' | 'appointment' | 'visit' | 'reschedule';
export type ViewType = 'file' | 'estimateFile' | 'invoice' | 'email' | 'analysis' | 'check' | 'authProof' | 'payProof' | 'payEmail';
export type SendTo = 'contractor' | 'homeowner' | 'insurance';
export interface TimelineItem {
  id: string;
  kind: TimelineKind;
  at: string;
  actor: string | null;
  actorSource: 'user' | 'monday' | 'system';
  titleKey: string;
  titleParams?: Record<string, string | number>;
  note?: string | null;
  amountCents?: number | null;
  details?: Array<{ labelKey?: string; label?: string; value: string; valueColor?: 'ours' | 'theirs' | 'money'; ageText?: string; tone?: 'green' | 'red' }>;
  badges?: Array<{ text: string; tone: 'green' | 'red' | 'blue' }>;
  missing?: Array<{ label: string; amount: string }>;
  views?: Array<{ type: ViewType; id?: string }>;
  // chips que abren en OTRA pestaña (Google Maps, video del ATR, fotos): lo que no se puede
  // embeber en el iframe del visor. OJO: tipo duplicado a mano con supplements/timeline.ts.
  links?: Array<{ label: string; url: string; icon?: string }>;
  // suprime "Send to CO/HO" aunque haya documento (invoice en draft: solo el issued se manda)
  noSend?: boolean;
  // nodos de STATUS DE PAGO: días en ese estado + si es el estado actual del claim
  durationDays?: number | null;
  current?: boolean;
  // ancla estable: el <li> se renderiza con id={`tl-${anchor}`} para poder linkear a este
  // nodo desde otra parte de la pantalla (ej. la reconciliación → el pago en el tracker).
  anchor?: string;
  workedSeconds?: number | null;
  /** 'full' = Approved (el seguro aprobó) · 'repair' = Approved for repair (no cuenta como aprobado). */
  approval?: 'full' | 'repair';
}
const VALUE_COLOR: Record<string, string> = { ours: '#3b82f6', theirs: '#f59e0b', money: 'var(--green)' };
// Lo que devuelve GET /api/claims/:id/emails/:emailId: la misma forma que pinta EmailCard (el hilo
// compartido), con lo que el modal necesita ya resuelto por el backend — quién lo mandó, el
// seguimiento entregado/abierto/clic y los adjuntos con su mime.
type EmailFull = EmailLike & {
  subject: string | null; from_address: string | null; to_address: string | null; cc_address: string | null;
  body: string | null; status: string | null; author: string | null;
  delivered_at: string | null; opened_at: string | null; clicked_at: string | null;
  attachments: EmailAttachment[];
};
type PayEmailRow = { id: string; recipient: string | null; recipient_type: string | null; subject: string | null; status: string | null; sent_at: string };

const ICON: Record<string, LucideIcon> = {
  'stage.firstEstimate': Rocket, 'stage.reviewEstimate': Search, 'stage.supplementing': Plus, 'stage.resolved': Flag,
  'sketch.done': Ruler, 'estimate.done': FileText, 'estimate.received': Inbox, 'estimate.rebuild': Hammer, 'estimate.sent': Mail, 'estimate.sentExternal': Send,
  'analysis.done': Bot, 'update.posted': MessageSquare,
  'check.received': Landmark, 'check.deposited': Banknote, 'check.available': CheckCircle2, 'check.bounced': AlertTriangle,
  'invoice.created': Receipt, 'invoice.submitted': Send, 'invoice.approved': ThumbsUp, 'invoice.issued': Mail,
  'payment.authorizedContractor': LockOpen, 'payment.authorizedHomeowner': LockOpen, 'payment.paidContractor': Banknote, 'payment.paidHomeowner': Banknote, 'payment.voided': Ban,
  // status de pago (a dónde fue el invoice tras emitirse)
  'st.inReview': Search, 'st.toRedo': RotateCcw, 'st.waitingClear': Hourglass, 'st.readyToPay': Banknote, 'st.generic': Pin,
  // resumen de pago explicado, enviado por email
  'summary.sentHomeowner': Mailbox, 'summary.sentContractor': Mailbox,
  'coc.sent': ScrollText,
  'approval.full': CheckCircle2, 'approval.repair': Hammer,
  // timeline de INSPECCIONES: llegada/filing, pedidos de visita, citas, visitas y reagendas
  'arrived': Home, 'filed': Mail,
  'request.inspection': Hand, 'request.atr': Video, 'request.reinspection': RotateCcw,
  'request.check_pickup': Banknote, 'request.material_sample': Box, 'request.reschedule': CalendarDays, 'request.made': Hand,
  'appt.booked.inspection': CalendarDays, 'appt.booked.atr': Video, 'appt.booked.adjuster_meeting': Handshake,
  'appt.booked.check_pickup': Banknote, 'appt.booked.material_sample': Box, 'appt.booked.repair_video': Clapperboard, 'appt.booked.custom': CalendarDays,
  'visit.completed': CheckCircle2, 'visit.inProgress': Construction, 'visit.noShow': DoorClosed,
  'resched.done': CalendarDays, 'resched.fieldRequest': Car, 'resched.reassigned': Shuffle,
};
const COLOR: Record<TimelineKind, string> = {
  stage: 'var(--accent)', sketch: '#f59e0b', estimate: '#3b82f6', analysis: '#8b5cf6', email: '#14b8a6',
  update: 'var(--muted)', check: '#10b981', invoice: '#6366f1', payment: 'var(--green)', payStatus: '#0ea5e9',
  approval: 'var(--green)',
  request: '#f59e0b', appointment: '#3b82f6', visit: 'var(--green)', reschedule: 'var(--red)',
};
const VIEW_LABEL: Record<string, string> = {
  file: 'tl.viewDoc', estimateFile: 'tl.viewEstimate', invoice: 'tl.viewInvoice', email: 'tl.viewEmail', check: 'tl.viewCheck', authProof: 'tl.viewAuthProof', payProof: 'tl.viewPayProof', payEmail: 'tl.viewEmail', analysis: 'tl.viewAnalysis',
};
const VIEW_ICON: Record<string, LucideIcon> = { file: FileText, estimateFile: FileText, invoice: Receipt, email: Mail, check: Landmark, authProof: PenLine, payProof: Receipt, payEmail: Mail, analysis: Search };
// tipos de view que son un DOCUMENTO adjuntable (para "Send to CO/HO/Insurance")
const DOC_TYPES: ViewType[] = ['file', 'estimateFile', 'invoice', 'check', 'authProof', 'payProof'];
const RECIPIENT_LABEL: Record<SendTo, string> = { contractor: 'tl.co', homeowner: 'tl.ho', insurance: 'tl.insurance' };

function dur(s: number): string {
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
  if (d) return `${d}d ${h}h`;
  if (h) return `${h}h ${m}m`;
  return `${Math.max(1, m)}m`;
}

export function TimelineView({
  claimId, items, error, tPrefix, title, subtitle, emptyText, sendDept, sendRecipients,
}: {
  claimId: string;
  items: TimelineItem[] | null;
  error?: boolean;
  tPrefix: string;
  title: string;
  subtitle?: string;
  emptyText: string;
  sendDept: 'payments' | 'supplements';
  sendRecipients: SendTo[];
}) {
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const [viewer, setViewer] = useState<{ title: string; src: string } | null>(null);
  const [email, setEmail] = useState<EmailFull | null>(null);
  const [payLog, setPayLog] = useState<PayEmailRow[] | null>(null);
  const [send, setSend] = useState<{ view: { type: ViewType; id?: string }; itemTitle: string; to: SendTo } | null>(null);
  const [busy, setBusy] = useState(false);
  const locale = lang === 'es' ? 'es-US' : 'en-US';

  async function openView(v: { type: ViewType; id?: string }, itemTitle: string) {
    setBusy(true);
    try {
      if (v.type === 'file' && v.id) setViewer({ title: itemTitle, src: `/api/files/${v.id}/inline` });
      else if (v.type === 'estimateFile' && v.id) {
        const r = await api.get<{ url: string }>(`/api/estimates/${v.id}/file-url`);
        setViewer({ title: itemTitle, src: r.url });
      } else if (v.type === 'invoice' && v.id) setViewer({ title: itemTitle, src: `/api/invoices/${v.id}/pdf` });
      else if (v.type === 'email' && v.id) {
        const r = await api.get<{ email: EmailFull }>(`/api/claims/${claimId}/emails/${v.id}`);
        setEmail(r.email);
      } else if (v.type === 'check' && v.id) {
        const r = await api.get<{ url: string }>(`/api/checks/${v.id}/file-url`);
        setViewer({ title: itemTitle, src: r.url });
      } else if (v.type === 'authProof') {
        const r = await api.get<{ hasAuth: boolean; url: string | null }>(`/api/claims/${claimId}/contractor-authorization`);
        if (r.url) setViewer({ title: itemTitle, src: r.url });
      } else if (v.type === 'payProof' && v.id) {
        const r = await api.get<{ url: string }>(`/api/payments/${v.id}/proof-url`);
        setViewer({ title: itemTitle, src: r.url });
      } else if (v.type === 'payEmail' && v.id) {
        const r = await api.get<{ items?: PayEmailRow[] } | PayEmailRow[]>(`/api/payments/${v.id}/emails`);
        setPayLog(Array.isArray(r) ? r : (r.items ?? []));
      } else if (v.type === 'analysis') {
        // análisis completo embebido (PDF del estimator guide), como se ve el estimado
        setViewer({ title: itemTitle, src: `/api/claims/${claimId}/negotiation/estimator-guide.pdf` });
      }
    } catch { /* silencioso */ } finally { setBusy(false); }
  }

  const who = (it: TimelineItem): string => (it.actorSource === 'monday' ? t('tl.viaMonday') : it.actor || t('tl.system'));

  return (
    <div style={{ display: 'grid', gap: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span style={{ fontSize: 'var(--fs-base)', fontWeight: 'var(--fw-bold)' }}>{title}</span>
        {subtitle && <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{subtitle}</span>}
      </div>

      {error && <div className="muted" style={{ fontSize: 'var(--fs-sm)' }}>{t('tl.error')}</div>}
      {!items && !error && <div className="muted" style={{ fontSize: 'var(--fs-sm)' }}>…</div>}
      {items && items.length === 0 && <div className="muted" style={{ fontSize: 'var(--fs-sm)', padding: '12px 0' }}>{emptyText}</div>}

      {items && items.length > 0 && (
        <ol className="sup-tl">
          {items.map((it, i) => {
            const itemTitle = t(`${tPrefix}.${it.titleKey}`, it.titleParams);
            const docView = it.views?.find((v) => DOC_TYPES.includes(v.type));
            const NodeIcon = ICON[it.titleKey];
            return (
              <li key={it.id} id={it.anchor ? `tl-${it.anchor}` : undefined}
                  className={`sup-tl-item task-row${it.approval ? ` sup-tl-${it.approval}` : ''}`}
                  style={{ animationDelay: `${Math.min(i * 45, 700)}ms`, scrollMarginTop: 70 }}>
                {/* Approved for repair NO es una aprobación para la firma: va en ámbar, no en verde. */}
                <div className="sup-tl-node" style={{ background: it.approval === 'repair' ? '#f59e0b' : COLOR[it.kind] }} aria-hidden>{NodeIcon ? <NodeIcon size={15} strokeWidth={1.75} /> : '•'}</div>
                <div className="sup-tl-card">
                  <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-bold)', color: 'var(--ink)', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    {itemTitle}
                    {/* EL estimado que quedó aprobado: se distingue a simple vista del resto. */}
                    {it.kind === 'estimate' && it.approval === 'full' && (
                      <span className="badge green" style={{ fontSize: 'var(--fs-2xs)' }}><CheckCircle2 size={12} strokeWidth={1.75} /> {t('tl.theApproved')}</span>
                    )}
                  </div>
                  <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginTop: 1 }}>
                    <span style={{ fontWeight: 600 }}>{who(it)}</span>
                    {' · '}{fmtDateTimeEastern(it.at, locale)}
                    {it.actorSource === 'monday' && <span className="badge blue" style={{ fontSize: 'var(--fs-2xs)', marginLeft: 6 }}>Monday</span>}
                  </div>
                  {/* O&P y otros badges cortos */}
                  {it.badges && it.badges.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 5 }}>
                      {it.badges.map((b, bi) => (
                        <span key={bi} className="badge" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', background: b.tone === 'red' ? 'var(--red-soft)' : b.tone === 'blue' ? 'var(--info-soft)' : 'var(--green-soft)', color: b.tone === 'red' ? 'var(--red)' : b.tone === 'blue' ? 'var(--info)' : 'var(--green)' }}>
                          {/* el 💰 es SOLO del badge de O&P (plata); los demás (Canceled, approx…) van sin él */}
                          {b.text === 'O&P' ? <><Coins size={12} strokeWidth={1.75} /> {t('tl.opBadge')}</> : b.text}
                        </span>
                      ))}
                    </div>
                  )}
                  {/* filas de detalle: montos (Proactive/Insurance/diferencia) + price list, alineadas a la IZQUIERDA */}
                  {it.details && it.details.length > 0 && (
                    <div style={{ display: 'grid', gap: 3, marginTop: 5, padding: '7px 9px', background: 'var(--surface-2)', borderRadius: 8 }}>
                      {it.details.map((d, di) => (
                        <div key={di} style={{ display: 'flex', alignItems: 'baseline', gap: 8, fontSize: 'var(--fs-sm)', flexWrap: 'wrap' }}>
                          <span className="muted" style={{ minWidth: 92 }}>{d.label ?? t(d.labelKey ?? '')}</span>
                          <span style={{ fontWeight: 800, fontFamily: 'var(--font-mono)', letterSpacing: -0.2, color: d.valueColor ? VALUE_COLOR[d.valueColor] : 'var(--ink)' }}>{d.value}</span>
                          {d.ageText && (
                            <span className="badge" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', background: d.tone === 'red' ? 'var(--red-soft)' : 'var(--green-soft)', color: d.tone === 'red' ? 'var(--red)' : 'var(--green)' }}>{d.ageText}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {/* partidas que el seguro NO paga (reemplaza el resumen largo, difícil de leer) */}
                  {it.missing && it.missing.length > 0 && (
                    <div style={{ marginTop: 6 }}>
                      <div style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', color: 'var(--red)', marginBottom: 3 }}><AlertTriangle size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('tl.missingTitle')}</div>
                      <div style={{ display: 'grid', gap: 2 }}>
                        {it.missing.map((m, mi) => (
                          <div key={mi} style={{ display: 'flex', alignItems: 'baseline', gap: 8, fontSize: 'var(--fs-xs)' }}>
                            <span style={{ fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--red)', whiteSpace: 'nowrap', minWidth: 78, textAlign: 'right' }}>{m.amount}</span>
                            <span style={{ minWidth: 0 }}>{m.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {it.note && <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink)', marginTop: 4, whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>{it.note}</div>}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6, alignItems: 'center' }}>
                    {it.amountCents != null && (
                      <span className="badge" style={{ fontSize: 'var(--fs-2xs)', background: 'var(--green-soft)', color: 'var(--green)', fontFamily: 'var(--font-mono)' }}>
                        {it.kind === 'analysis' ? `${t('tl.gap')}: ` : ''}{formatUsd(it.amountCents)}
                      </span>
                    )}
                    {it.workedSeconds != null && (
                      <span className="badge" style={{ fontSize: 'var(--fs-2xs)', background: 'var(--surface-2)', color: 'var(--muted)' }}><Timer size={12} strokeWidth={1.75} /> {t('tl.worked')}: {dur(it.workedSeconds)}</span>
                    )}
                    {/* días en este status de pago; azul + "actual" si es el estado vigente */}
                    {it.durationDays != null && (
                      <span className="badge" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', background: it.current ? 'var(--info-soft)' : 'var(--surface-2)', color: it.current ? 'var(--info)' : 'var(--muted)' }}>
                        {it.current
                          ? <><Hourglass size={12} strokeWidth={1.75} /> {t('tl.statusCurrent', { days: it.durationDays })}</>
                          : <><Timer size={12} strokeWidth={1.75} /> {t('tl.statusDays', { days: it.durationDays })}</>}
                      </span>
                    )}
                    {(it.views ?? []).map((v, vi) => {
                      const ViewIcon = VIEW_ICON[v.type] ?? Eye;
                      return (
                        <button key={vi} className="btn ghost sm" style={{ fontSize: 'var(--fs-2xs)' }} disabled={busy} onClick={() => void openView(v, itemTitle)}>
                          <ViewIcon size={14} strokeWidth={1.75} /> {t(VIEW_LABEL[v.type] ?? 'tl.viewDoc')}
                        </button>
                      );
                    })}
                    {/* Links externos (mapa del check-in, video del ATR, fotos): abren en pestaña,
                        no se pueden embeber en el iframe del visor (Google Maps/YouTube lo bloquean). */}
                    {(it.links ?? []).map((l, li) => (
                      <a key={`lnk-${li}`} className="btn ghost sm" style={{ fontSize: 'var(--fs-2xs)', textDecoration: 'none' }}
                        href={l.url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                        {l.icon ?? <Link2 size={14} strokeWidth={1.75} />} {l.label}
                      </a>
                    ))}
                    {/* Enviar el documento del item por email desde el correo del depto, a cada destinatario.
                        noSend suprime el botón (ej. invoice en draft/review/approved: solo el issued se manda). */}
                    {docView && !it.noSend && sendRecipients.map((r) => (
                      <button key={r} className="btn ghost sm" style={{ fontSize: 'var(--fs-2xs)' }} onClick={() => setSend({ view: docView, itemTitle, to: r })}>
                        <Send size={14} strokeWidth={1.75} /> {t(RECIPIENT_LABEL[r])}
                      </button>
                    ))}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}

      {/* Visor EMBEBIDO (iframe) para estimado/invoice/cheque/comprobante/autorización */}
      {viewer && (
        <div onClick={() => setViewer(null)} role="dialog" aria-modal="true" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 4000, display: 'grid', placeItems: 'center', padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()} className="card" style={{ width: 'min(1000px, 94vw)', height: '90vh', display: 'flex', flexDirection: 'column', gap: 8, padding: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <div style={{ fontWeight: 'var(--fw-bold)', fontSize: 'var(--fs-sm)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}><Paperclip size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {viewer.title}</div>
              <div style={{ display: 'flex', gap: 6 }}>
                <a href={viewer.src} target="_blank" rel="noreferrer" className="btn ghost sm" style={{ fontSize: 'var(--fs-2xs)' }}>↗ {t('tl.openTab')}</a>
                <button className="btn ghost sm" onClick={() => setViewer(null)}><X size={14} strokeWidth={1.75} /></button>
              </div>
            </div>
            <iframe title={viewer.title} src={viewer.src} style={{ flex: 1, width: '100%', border: '1px solid var(--line)', borderRadius: 8, background: '#fff' }} />
          </div>
        </div>
      )}

      {/* Email completo (claim_emails) embebido: la MISMA tarjeta del hilo de correos (EmailCard),
          con quién → a quién, el seguimiento entregado/abierto/clic y los adjuntos (abrir/descargar).
          El asunto ya va en la cabecera del modal, por eso la tarjeta no lo repite. */}
      {email && (
        <div onClick={() => setEmail(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 4000, display: 'grid', placeItems: 'center', padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} className="card" style={{ maxWidth: 720, width: '100%', maxHeight: '80vh', overflowY: 'auto', display: 'grid', gap: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 8 }}>
              <div style={{ fontWeight: 'var(--fw-bold)', fontSize: 'var(--fs-base)' }}><Mail size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {email.subject || t('tl.noSubject')}</div>
              <button className="btn ghost sm" onClick={() => setEmail(null)}><X size={14} strokeWidth={1.75} /></button>
            </div>
            <EmailCard message={email} defaultOpen showSubject={false} onReply={(m) => navigate(rutaParaResponder(claimId, m))} />
          </div>
        </div>
      )}

      {/* Log de emails de pago (no guardan body: mostramos a quién/asunto/cuándo/estado) */}
      {payLog && (
        <div onClick={() => setPayLog(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 4000, display: 'grid', placeItems: 'center', padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} className="card" style={{ maxWidth: 520, width: '100%', maxHeight: '80vh', overflowY: 'auto', display: 'grid', gap: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <div style={{ fontWeight: 'var(--fw-bold)', fontSize: 'var(--fs-base)' }}><Mail size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('tl.payEmails')}</div>
              <button className="btn ghost sm" onClick={() => setPayLog(null)}><X size={14} strokeWidth={1.75} /></button>
            </div>
            {payLog.length === 0 && <div className="muted" style={{ fontSize: 'var(--fs-sm)' }}>{t('tl.noPayEmails')}</div>}
            {payLog.map((e) => (
              <div key={e.id} style={{ border: '1px solid var(--line)', borderRadius: 8, padding: '8px 10px', display: 'grid', gap: 2 }}>
                <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)' }}>{e.subject || t('tl.noSubject')}</div>
                <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>
                  {t('tl.to')}: {e.recipient || '—'} · {fmtDateTimeEastern(e.sent_at, locale)}
                  {e.status && <span className="badge" style={{ fontSize: 'var(--fs-2xs)', marginLeft: 6, background: 'var(--surface-2)' }}>{e.status}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick-send: manda el documento del item al CO/HO/Seguro desde el correo del depto */}
      {send && <QuickSend claimId={claimId} dept={sendDept} view={send.view} itemTitle={send.itemTitle} recipients={sendRecipients} initialTo={send.to} onClose={() => setSend(null)} />}
    </div>
  );
}

const RECIPIENT_ICON: Record<SendTo, LucideIcon> = { contractor: HardHat, homeowner: Home, insurance: Landmark };

/** Modal compacto para enviar a CO/HO/Seguro: prellena asunto/cuerpo, el usuario revisa y envía. */
function QuickSend({ claimId, dept, view, itemTitle, recipients, initialTo, onClose }: { claimId: string; dept: 'payments' | 'supplements'; view: { type: string; id?: string }; itemTitle: string; recipients: SendTo[]; initialTo: SendTo; onClose: () => void }) {
  const { t } = useI18n();
  const [to, setTo] = useState<SendTo>(initialTo);
  const [subject, setSubject] = useState(itemTitle);
  const [body, setBody] = useState(t('tl.sendBodyDefault'));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  async function submit() {
    if (!subject.trim() || !body.trim()) return;
    setBusy(true); setErr(null);
    try {
      await api.post(`/api/claims/${claimId}/timeline/send`, { department: dept, to, view, subject: subject.trim(), body: body.trim() });
      setOk(true);
      setTimeout(onClose, 900);
    } catch (e) {
      if (e instanceof ApiError) {
        const m = t(`tl.sendErr.${e.code}`);
        setErr(m.startsWith('tl.sendErr.') ? t('tl.sendErrGeneric') : m);
      } else setErr(String(e));
    } finally { setBusy(false); }
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 4000, display: 'grid', placeItems: 'center', padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} className="card" style={{ maxWidth: 480, width: '100%', display: 'grid', gap: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <div style={{ fontWeight: 'var(--fw-bold)', fontSize: 'var(--fs-base)' }}><Send size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('tl.sendTitle')}</div>
          <button className="btn ghost sm" onClick={onClose}><X size={14} strokeWidth={1.75} /></button>
        </div>
        <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('tl.sendFrom', { dept: t(`tl.dept.${dept}`) })}</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {recipients.map((r) => {
            const RecipientIcon = RECIPIENT_ICON[r];
            return (
              <button key={r} className={`btn sm ${to === r ? '' : 'ghost'}`} onClick={() => setTo(r)}>
                <RecipientIcon size={14} strokeWidth={1.75} /> {t(r === 'contractor' ? 'tl.co' : r === 'insurance' ? 'tl.insurance' : 'tl.ho')}
              </button>
            );
          })}
        </div>
        <input className="input" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder={t('tl.subject')} />
        <textarea className="input" rows={4} value={body} onChange={(e) => setBody(e.target.value)} placeholder={t('tl.message')} />
        <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}><Paperclip size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('tl.willAttach')}</div>
        {err && <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--red)' }}>{err}</div>}
        {ok && <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--green)' }}><Check size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('tl.sent')}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn ghost sm" onClick={onClose}>{t('common.cancel')}</button>
          <button className="btn sm" disabled={busy || ok || !subject.trim() || !body.trim()} onClick={() => void submit()}>{busy ? '…' : t('tl.send')}</button>
        </div>
      </div>
    </div>
  );
}
